import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  accountAccessLabel,
  accountAccessSubLabel,
  adminApi,
  amazonAccountKey,
  assignableAccountKey,
  brandAccountKey,
  flipkartAccountKey,
  splitSelectedAccountKeys,
  type AdminUserListItem,
  type AssignableAccount,
} from '@/api/adminApi';
import { useAuth } from '@/contexts/AuthContext';
import { SignOutButton } from '@/components/auth/SignOutButton';

function roleLabel(user: AdminUserListItem): string {
  return user.global_roles.includes('super_admin') ? 'Super admin' : 'Account owner';
}

function groupedAssignableAccounts(accounts: AssignableAccount[]) {
  const amazon = accounts.filter(
    (account): account is Extract<AssignableAccount, { platform: 'amazon' }> =>
      account.platform === 'amazon'
  );
  const flipkart = accounts.filter(
    (account): account is Extract<AssignableAccount, { platform: 'flipkart' }> =>
      account.platform === 'flipkart'
  );
  const brands = accounts.filter(
    (account): account is Extract<AssignableAccount, { platform: 'brand' }> =>
      account.platform === 'brand'
  );
  return { amazon, flipkart, brands };
}

function AccountAccessPicker({
  accounts,
  loading,
  selected,
  onToggle,
}: {
  accounts: AssignableAccount[];
  loading?: boolean;
  selected: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) {
  const { amazon, flipkart, brands } = groupedAssignableAccounts(accounts);
  if (loading) {
    return <div className="p-4 text-center text-foreground-muted">Loading…</div>;
  }
  if (accounts.length === 0) {
    return <div className="p-4 text-center text-foreground-muted">No accounts found</div>;
  }

  const groups: Array<{ label: string; items: AssignableAccount[] }> = [];
  if (amazon.length > 0) groups.push({ label: 'Amazon', items: amazon });
  if (flipkart.length > 0) groups.push({ label: 'Flipkart', items: flipkart });
  if (brands.length > 0) groups.push({ label: 'Brands', items: brands });

  return (
    <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y">
      {groups.map((group) => (
        <div key={group.label}>
          <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-muted bg-surface-muted">
            {group.label}
          </div>
          {group.items.map((account) => {
            const key = assignableAccountKey(account);
            return (
              <label
                key={key}
                className="flex items-start gap-2 p-2 hover:bg-surface-muted cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => onToggle(key)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium block">{accountAccessLabel(account)}</span>
                  <span className="text-xs text-foreground-muted font-mono">
                    {accountAccessSubLabel(account)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function UserFormModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [globalRole, setGlobalRole] = useState<'super_admin' | 'account_owner'>('account_owner');
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [profileSearch, setProfileSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: profilesRes, isLoading: profilesLoading } = useQuery({
    queryKey: ['admin-profiles', profileSearch],
    queryFn: () => adminApi.listAssignableProfiles(profileSearch || undefined),
  });

  const accounts = profilesRes?.data ?? [];

  const createMutation = useMutation({
    mutationFn: () => {
      const { profileIds, flipkartAccounts, brandIds } = splitSelectedAccountKeys(selectedAccounts);
      return adminApi.createUser({
        email,
        password,
        global_role: globalRole,
        profile_ids: globalRole === 'account_owner' ? profileIds : [],
        flipkart_accounts: globalRole === 'account_owner' ? flipkartAccounts : [],
        brand_ids: globalRole === 'account_owner' ? brandIds : [],
      });
    },
    onSuccess: () => {
      onCreated();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const toggleAccount = (key: string) => {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface border border-border shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Create user</h2>
        </div>
        <form
          className="p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            createMutation.mutate();
          }}
        >
          <label className="block text-sm">
            <span className="font-medium text-foreground">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-foreground">Password</span>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-foreground">Role</span>
            <select
              value={globalRole}
              onChange={(e) => setGlobalRole(e.target.value as 'super_admin' | 'account_owner')}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            >
              <option value="account_owner">Account owner</option>
              <option value="super_admin">Super admin</option>
            </select>
          </label>

          {globalRole === 'account_owner' && (
            <div className="text-sm">
              <span className="font-medium text-foreground">Account access</span>
              <input
                type="search"
                placeholder="Search accounts…"
                value={profileSearch}
                onChange={(e) => setProfileSearch(e.target.value)}
                className="mt-1 mb-2 w-full rounded-lg border border-border px-3 py-2 text-sm"
              />
              <AccountAccessPicker
                accounts={accounts}
                loading={profilesLoading}
                selected={selectedAccounts}
                onToggle={toggleAccount}
              />
            </div>
          )}

          {error && <p className="text-sm text-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creating…' : 'Create user'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SetPasswordModal({
  userId,
  userEmail,
  onClose,
}: {
  userId: string;
  userEmail: string;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => adminApi.updateUser(userId, { password }),
    onSuccess: () => onClose(),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface border border-border shadow-lg">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Set password</h2>
          <p className="text-sm text-foreground-muted mt-1">{userEmail}</p>
        </div>
        <form
          className="p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (password !== confirmPassword) {
              setError('Passwords do not match');
              return;
            }
            saveMutation.mutate();
          }}
        >
          <label className="block text-sm">
            <span className="font-medium text-foreground">New password</span>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-foreground">Confirm password</span>
            <input
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
          </label>

          {error && <p className="text-sm text-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : 'Save password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ManageGrantsModal({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [profileSearch, setProfileSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const { data: userRes } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () => adminApi.getUser(userId),
  });

  const { data: profilesRes, isLoading: profilesLoading } = useQuery({
    queryKey: ['admin-profiles', profileSearch],
    queryFn: () => adminApi.listAssignableProfiles(profileSearch || undefined),
  });

  const user = userRes?.data;
  const accounts = profilesRes?.data ?? [];

  useEffect(() => {
    if (user && !initialized) {
      const keys = [
        ...user.profile_grants.map((g) => amazonAccountKey(g.profile_id)),
        ...(user.flipkart_grants ?? []).map((g) =>
          flipkartAccountKey(g.credential_id, g.ad_account_id)
        ),
        ...(user.brand_grants ?? []).map((g) => brandAccountKey(g.brand_id)),
      ];
      setSelected(new Set(keys));
      setInitialized(true);
    }
  }, [user, initialized]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const { profileIds, flipkartAccounts, brandIds } = splitSelectedAccountKeys(selected);
      return adminApi.replaceProfileAccess(
        userId,
        profileIds.map((profile_id) => ({ profile_id, role_code: 'account_owner' })),
        flipkartAccounts.map((row) => ({ ...row, role_code: 'account_owner' })),
        brandIds.map((brand_id) => ({ brand_id, role_code: 'account_owner' }))
      );
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-users'] });
      await qc.invalidateQueries({ queryKey: ['admin-user', userId] });
      onClose();
    },
  });

  const toggle = (pid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-surface border border-border shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Manage account access</h2>
          <p className="text-sm text-foreground-muted mt-1">{user?.email}</p>
        </div>
        <div className="p-6 space-y-4">
          <input
            type="search"
            placeholder="Search accounts…"
            value={profileSearch}
            onChange={(e) => setProfileSearch(e.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2 text-sm"
          />
          <AccountAccessPicker
            accounts={accounts}
            loading={profilesLoading}
            selected={selected}
            onToggle={toggle}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminUsersPage() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [manageUserId, setManageUserId] = useState<string | null>(null);
  const [setPasswordUser, setSetPasswordUser] = useState<{ id: string; email: string } | null>(
    null
  );
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: () => adminApi.listUsers({ search: search || undefined }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => adminApi.updateUser(userId, { is_active: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const users = data?.data?.items ?? [];

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ProductBrand variant="light" showTagline={false} />
            <div className="hidden sm:flex items-center gap-2 border-l border-border pl-4">
              <Users className="w-5 h-5 text-brand shrink-0" aria-hidden />
              <h1 className="text-headline-md font-semibold text-foreground">User management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link
              to="/admin/account-metrics"
              className="text-body-sm text-brand hover:underline font-medium"
            >
              Account metrics
            </Link>
            {isSuperAdmin ? (
              <>
                <Link
                  to="/admin/report-coverage"
                  className="text-body-sm text-brand hover:underline font-medium"
                >
                  Report coverage
                </Link>
                <Link
                  to="/admin/stream-subscriptions"
                  className="text-body-sm text-brand hover:underline font-medium"
                >
                  Stream subscriptions
                </Link>
                <Link
                  to="/admin/settings"
                  className="text-body-sm text-brand hover:underline font-medium"
                >
                  SP-API
                </Link>
              </>
            ) : null}
            <Link to="/accounts" className="text-body-sm text-foreground-muted hover:text-foreground">
              Accounts
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <input
            type="search"
            placeholder="Search by email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg border border-border px-3 py-2 text-sm w-full max-w-xs"
          />
          <Button type="button" onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4" aria-hidden />
            Create user
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="w-10 h-10" />
          </div>
        ) : error ? (
          <p className="text-error">{(error as Error).message}</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-foreground-muted">
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Accounts</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-3">{user.email}</td>
                    <td className="px-4 py-3">{roleLabel(user)}</td>
                    <td className="px-4 py-3">
                      {user.global_roles.includes('super_admin')
                        ? 'All'
                        : (user.profile_grant_count ?? 0)}
                    </td>
                    <td className="px-4 py-3">{user.is_active ? 'Active' : 'Deactivated'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setSetPasswordUser({ id: user.id, email: user.email })}
                        >
                          Set password
                        </Button>
                        {!user.global_roles.includes('super_admin') && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => setManageUserId(user.id)}
                          >
                            Accounts
                          </Button>
                        )}
                        {user.is_active && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => deactivateMutation.mutate(user.id)}
                          >
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showCreate && (
        <UserFormModal
          onClose={() => setShowCreate(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
        />
      )}
      {manageUserId && (
        <ManageGrantsModal userId={manageUserId} onClose={() => setManageUserId(null)} />
      )}
      {setPasswordUser && (
        <SetPasswordModal
          userId={setPasswordUser.id}
          userEmail={setPasswordUser.email}
          onClose={() => setSetPasswordUser(null)}
        />
      )}
    </div>
  );
}
